import { Injectable } from '@angular/core';
import { AppConfig } from '../../shared/config/app-config';
import { Aggregator, SuperAggregator } from '../../shared/models/business.model';
import { PagedRequest } from '../../shared/models/paged-request.model';
import { ApiClientHelper } from '../helpers/api-client.helper';
import { StringHelper } from '../helpers/strings.helper';
import { LocalStorageService } from './local-storage.service';

@Injectable({
    providedIn: 'root',
})
export class BRMDeliveryService {
    private BrmCode: string;
    private completedDeliveryRequests: PagedDeliveryRequest;
    private pendingDeliveryRequests: PagedDeliveryRequest;
    private failedDeliveryRequests: PagedDeliveryRequest;
    private earningsData: DeliveryRequest[];
    private earningsStats: EarningsStats;
    private inventoryStats: InventoryStats;
    private inventoryCartons: CardCartonsModel[];
    private inventoryPackets: CardPacketsModel[];
    private inventoryCards: CardEnvelopeModel[];
    private inventoryCurrentPages: any;

    constructor(private apiClientHelper: ApiClientHelper, public config: AppConfig, private localStorage: LocalStorageService) { }

    showBrmCardSalesAgent() {
        return this.config.SHOW_BRM_CARD_SALES_AGENT;
    }

    brmCanRetrieveCards() {
        return this.config.BRM_CAN_RETRIEVE_CARDS;
    }

    showBrmCardNewUI() {
        return this.config.SHOW_BRM_CARD_NEW_UI;
    }

    canPushCardsToSalesAgent() {
        return this.config.SHOW_BRM_PUSH_CARDS_TO_SALES_AGENT;
    }

    getBrmPushToSalesAgentCardCount() {
        return this.config.BRM_PUSH_TO_SALES_AGENT_CARD_COUNT;
    }

    canUseFlexibleCardCost() {
        return this.config.BRM_CAN_USE_FLEXIBLE_CARD_COST;
    }
    canUseConfigFlexibleCardCost() {
        return this.config.BRM_CAN_USE_CONFIG_FLEXIBLE_CARD_COST;
    }

    setBrmCode(code) {
        this.BrmCode = code;
    }

    setCompletedDeliveryRequests(requests: PagedDeliveryRequest) {
        this.completedDeliveryRequests = requests;
    }
    setPendingDeliveryRequests(requests: PagedDeliveryRequest) {
        this.pendingDeliveryRequests = requests;
    }
    setFailedDeliveryRequests(requests: PagedDeliveryRequest) {
        this.failedDeliveryRequests = requests;
    }

    setEarningsData(data: DeliveryRequest[]) {
        this.earningsData = data;
    }

    setSavedEarningsStats(data: EarningsStats) {
        this.earningsStats = data;
    }

    setInventoryStats(data: InventoryStats) {
        this.inventoryStats = data;
    }
    setInventoryCartons(data: CardCartonsModel[]) {
        this.inventoryCartons = data;
    }
    setInventoryPackets(data: CardPacketsModel[]) {
        this.inventoryPackets = data;
    }
    setInventoryCards(data: CardEnvelopeModel[]) {
        this.inventoryCards = data;
    }

    setInventoryCurrentPages(data: any) {
        this.inventoryCurrentPages = { ...this.inventoryCurrentPages, ...data };
    }

    getBrmCode() {
        return this.BrmCode;
    }

    getCompletedDeliveryRequests() {
        return this.completedDeliveryRequests;
    }
    getPendingDeliveryRequests() {
        return this.pendingDeliveryRequests;
    }
    getFailedDeliveryRequests() {
        return this.failedDeliveryRequests;
    }

    getEarningsData() {
        return this.earningsData;
    }

    getSavedEarningsStats() {
        return this.earningsStats;
    }

    getInventoryStats() {
        return this.inventoryStats;
    }

    getInventoryCartons() {
        return this.inventoryCartons;
    }
    getInventoryPackets() {
        return this.inventoryPackets;
    }
    getInventoryCards() {
        return this.inventoryCards;
    }

    getInventoryCurrentPages() {
        return this.inventoryCurrentPages;
    }

    getBrmRequests(request: any, param?: any): Promise<any> {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_LOGISTICS + `/api/v1/brm/cards/get-brm-requests?${params}`;
        return this.apiClientHelper.postForPromise(url, request);
    }

    getBrmRequestsStats(params: any): Promise<any> {
        const url = this.config.MONIEPOINT_LOGISTICS + `/api/v1/brm/cards/delivered-statistics`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    completeDeliveryRequest(params: any): Promise<any> {
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/card-delivery-requests/confirm`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    getBrmInventoryStats(): Promise<any> {
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/brm/card-inventory`;
        return this.apiClientHelper.getForPromise(url);
    }

    getBrmInventoryCartons(param: any): Promise<any> {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/aggregator/cartons?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getBrmInventoryPackets(param: any): Promise<any> {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/packet-inventory/aggregator/packets?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }
    getBrmInventoryPacketsByCartonRefs(cartonRef: any, param: any): Promise<any> {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/packet-inventory/aggregator/packets/carton-ref/${cartonRef}?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getBrmInventoryCards(param: any): Promise<any> {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/customer-card/aggregator/get-all-cards?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }
    getBrmInventoryCardsByPacketRef(packetRef: string, param: any): Promise<any> {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/customer-card/aggregator/get-all-cards/packet-ref/${packetRef}?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getAccountDetails(param: any): Promise<any> {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_LOGISTICS + `/api/v1/account/validate${params && '?' + params}`;
        return this.apiClientHelper.getForPromise(url);
    }
    getCustomerAccountDetails(param: any): Promise<any> {
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/business-owners/account-number/${param}`;
        return this.apiClientHelper.getForPromise(url);
    }
    getDefaultCardBatchCount(): Promise<any> {
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/sales-agents/card-batch-requests/config`;
        return this.apiClientHelper.getForPromise(url);
    }

    assignCardToBo(params: any): Promise<any> {
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/customer-card/push-request`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    assignCardToBoV2(params: any): Promise<any> {
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/card-push-requests/brm`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    assignCartonsToSalesAgent(params: any): Promise<any> {
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/aggregator/agent/request`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    assignCardToSalesAgent(params: any): Promise<any> {
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/brm/card-batch-requests`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    getBRMfromCode(code: any): Promise<Aggregator> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/aggregators/code/${code}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getComments(): Promise<any> {
        const url = this.config.MONIEPOINT_LOGISTICS + `/api/v1/logistics/comments`;
        return this.apiClientHelper.getForPromise(url);
    }

    abortDeliveryRequest(params: any): Promise<any> {
        const url = this.config.MONIEPOINT_LOGISTICS + `/api/v1/logistics/cards/failed-delivery`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    abortDeliveryRequestV2(taskId: string, params: { commentId: string; additionalComment: string }): Promise<any> {
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/brm/cards/tasks/${taskId}/delivery/fail`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    getPushedCards(param: any): Promise<any> {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/brm/card-pushed/business-owner-info?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }
    reassignCardTask(id: string, params: any): Promise<any> {
        const url = this.config.MONIEPOINT_LOGISTICS + `/api/v1/logistics/cards/task/reassign/${id}`;
        return this.apiClientHelper.postForPromise(url, params);
    }
    reassignGlobalCardTask(id: string, params: any): Promise<any> {
        const url = this.config.MONIEPOINT_LOGISTICS + `/api/v1/logistics/cards/task/reassign/brm/${id}`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    reassignGlobalCardTaskV2(taskId: string, params: { deliveryCode: string; newDeliveryMode: string }): Promise<any> {
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/brm/cards/tasks/${taskId}/reassign`;
        return this.apiClientHelper.postForPromise(url, params);
    }

    getGlobalRequests(param) {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_LOGISTICS + `/api/v1/logistics/cards/tasks/public-requests?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getGlobalRequestConfig() {
        const url = this.config.MONIEPOINT_LOGISTICS + `/api/v1/logistics/cards/tasks/public-requests/config`;
        return this.apiClientHelper.getForPromise(url);
    }
    getCustomerCardRequestHistory(param) {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/card-delivery-requests/brm?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }
    getCustomerPendingCardRequests(param) {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/cards/brm/card-pushed/business-owner-info?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }
    getAgentCardRequestHistory(param) {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/brm/card-batch-requests?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    approveSalesAgentCardRequest(data) {
        const url = this.config.MONIEPOINT_LOGISTICS + `/api/v1/logistics/cards/requests/approve-requests`;
        return this.apiClientHelper.postForObservable(url, data);
    }
    getCardRequestsHistory() {
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/stats/requests`;
        return this.apiClientHelper.getForPromise(url);
    }
    brmCollectCartonFromSalesAgent(cartonRef: string) {
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/carton-inventory/${cartonRef}/aggregator/collect`;
        return this.apiClientHelper.getForPromise(url);
    }
    getBrmCollectCartonFromSalesAgent(param) {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/brm/cartons?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getDeliveryRequestStats(param) {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/stats/brm/overview?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    getCardPushStats(param) {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/stats/brm/card-pushes?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

    showCardDistributionEfficiencyNewUI() {
        return this.config.SHOW_CARD_DISTRIBUTION_EFFICIENCY_NEW_UI;
    }

    getBrmMarketersMapping(param): Promise<any> {
        const params = StringHelper.toUrlString(param);
        const url = this.config.MONIEPOINT_CARDS + `/api/v1/brm/agents?${params}`;
        return this.apiClientHelper.getForPromise(url);
    }

}

export interface PagedDeliveryRequest {
    data: DeliveryRequest[],
    pageNumber: number,
    pageSize: number,
    totalElements: number,
    totalPages: number,
}

export interface DeliveryRequest {
    id: number;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    deliveryFee: number;
    city: string;
    deliveryItem: string;
    status: string;
    requestDate: Date | string;
    completionDate?: any;
    addressLine1: string;
    addressLine2: string;
    localGovernmentAreaId: number;
    expectedDeliveryDateCustomer: Date | string;
    expectedDeliveryDatePartner: Date | string;
    deliveryComment?: any;
    deliveryTATStatus: string;
    completedBy?: any;
    paymentReference: string;
    partnerAccountNumber?: any;
    cardSerialNumber?: any;
    alternatePhoneNumber?: any;
    cardDetails?: CardDetails;
    settlementDetails?: SettlementDetails;
    businessOwner?: BusinessOwner;
    requestCreatedOn?: any;
}

export interface CardDetails {
    cardType: any;
    activationDate: any;
    cardStatus: any;
}

export interface SettlementDetails {
    cardSaleSettlementAmount: any;
    deliveryFeeSettlementAmount: any;
    lateDeliveryPenaltyAmount: any;
    isLateDeliveryPenaltyApplied: any;
    deliveryPenaltyPercentage: any;
    expectedCardSettlementAmount: any;
    expectedDeliveryFeeSettlementAmount: any;
    lateDeliveryDeductionActive: any;
    lateDeliveryPenaltyApplied: any;
    settlementStatus: any;
    actualSettledAmount: any;
    expectedSettlementAmount: any;
}

export interface BusinessOwner {
    businessName: any;
    businessPhone: any;
    businessEmail: any;
}

export interface EarningsStats {
    noOfCardsDelivered: number;
    totalAmount: number;
}

export interface InventoryStats {
    totalCartons: number;
    totalPackets: number;
    totalCards: number;
}

export interface CardCartonsModel {
    cartonRef: string
    location: string
    numberOfPackets: number
    eventTime: string
    custodianSupremeAggregator: string
    custodianSuperAggregator: string
    custodianAggregator: string
    packetsRetrieved: boolean
    assigned: boolean
    canCollect: boolean
}

export interface CardPacketsModel {
    packetRef: string
    location: string
    eventTime: string
    custodianSupremeAggregator: string
    custodianSuperAggregator: string
    custodianAggregator: string
    custodianAgent: string
    cardsRetrieved: boolean
    assigned: boolean
    numberOfCards: number
}

export interface CardEnvelopeModel {
    serialNumber: string
    packetRef: string
    cartonRef: string
    daysInInventory: number
    assignedToCustomer: boolean
}

export class RequestFilter extends PagedRequest {
    'cartonRef'?: string;
    'packetRef'?: string;

    constructor(obj?) {
        super(obj);
        if (obj && obj['cartonRef']) this['cartonRef'] = obj && obj['cartonRef'] || null;
        if (obj && obj['packetRef']) this['packetRef'] = obj && obj['packetRef'] || null;
    }
}
